/**
 * Role Play Detail Screen
 *
 * Shows the full setup fields for a slot and exposes Open / Restart /
 * Remove actions. Restart and Remove are confirmed via ConfirmDialog.
 *
 * @see docs/specs/mobile/roleplay.mobile.md
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { SectionCard } from '../components/ui/SectionCard';
import { WebContainer } from '../components/ui/WebContainer';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { colors as appColors } from '../theme';
import { errorMessage } from '../generated/api/endpoint-errors';
import {
  useRolePlayStore,
  selectRolePlayMutating,
  selectRolePlayErrorInfo,
  selectLoadSlotDetail,
  selectRestartSlot,
  selectRemoveSlot,
} from '../store/rolePlayStore';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { selectLessonLanguage, selectSourceLocale, selectTargetLocale } from '../store/userPreferencesSelectors';
import { resolveBilingualContent, type BilingualContent } from '../types/lesson';

export function RolePlayDetailScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();
  const route = useRoute('RolePlayDetail');
  const slotId = route.params.slotId;

  const slot = useRolePlayStore(useCallback((s) => s.slots.find((x) => x.id === slotId) ?? null, [slotId]));
  const mutating = useRolePlayStore(selectRolePlayMutating);
  const errorInfo = useRolePlayStore(selectRolePlayErrorInfo);
  const loadSlotDetail = useRolePlayStore(selectLoadSlotDetail);
  const restartSlot = useRolePlayStore(selectRestartSlot);
  const removeSlot = useRolePlayStore(selectRemoveSlot);

  // Bilingual resolution
  const lessonLanguage = useUserPreferencesStore(selectLessonLanguage);
  const sourceLocale = useUserPreferencesStore(selectSourceLocale);
  const targetLocale = useUserPreferencesStore(selectTargetLocale);
  const resolve = useCallback(
    (content: BilingualContent | string) =>
      resolveBilingualContent(content, lessonLanguage, sourceLocale, targetLocale),
    [lessonLanguage, sourceLocale, targetLocale],
  );

  const [showRestartDialog, setShowRestartDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  // Fetch detail on mount if we don't already have scenarioDetails
  useEffect(() => {
    if (slot && !slot.scenarioDetails) {
      void loadSlotDetail(slotId);
    } else if (!slot) {
      void loadSlotDetail(slotId);
    }
    // Intentionally only run on mount / slotId change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotId]);

  const handleOpen = useCallback(() => {
    if (!slot) return;
    navigation.navigate('Chat', { sessionId: slot.conversationId });
  }, [navigation, slot]);

  const handleRestartConfirm = useCallback(async () => {
    setShowRestartDialog(false);
    if (!slot) return;
    const newConversationId = await restartSlot(slot.id);
    if (newConversationId) {
      navigation.navigate('Chat', { sessionId: newConversationId });
    }
  }, [restartSlot, slot, navigation]);

  const handleRemoveConfirm = useCallback(async () => {
    setShowRemoveDialog(false);
    if (!slot) return;
    const ok = await removeSlot(slot.id);
    if (ok) {
      navigation.goBack();
    }
  }, [removeSlot, slot, navigation]);

  // Missing slot (fetch failed or 404)
  if (!slot) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.background }}>
        <ScreenHeader title={t('roleplay.detail.title')} />
        <WebContainer variant="page" style={{ flex: 1 }}>
          <ScrollView testID="roleplay-detail-screen" contentContainerStyle={styles.content}>
            {errorInfo ? (
              <View
                testID="roleplay-detail-error-banner"
                style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error }]}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: 14 * fontScale, color: theme.text, ...ffs }}
                >
                  {errorMessage(errorInfo)}
                </Text>
              </View>
            ) : (
              <ActivityIndicator size="small" color={theme.primary} />
            )}
          </ScrollView>
        </WebContainer>
      </View>
    );
  }

  const firstSpeakerLabel = slot.firstSpeaker === 'user' ? t('roleplay.detail.firstSpeakerUser') : t('roleplay.detail.firstSpeakerAi');

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={t('roleplay.detail.title')} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        <ScrollView testID="roleplay-detail-screen" contentContainerStyle={styles.content}>
          <SectionCard title={resolve(slot.title)}>
            <Field
              testID="roleplay-detail-field-scenario"
              label={t('roleplay.detail.fieldScenario')}
              value={resolve(slot.scenario)}
            />
            {slot.scenarioDetails ? (
              <Field
                testID="roleplay-detail-field-details"
                label={t('roleplay.detail.fieldDetails')}
                value={resolve(slot.scenarioDetails)}
              />
            ) : null}
            <Field
              testID="roleplay-detail-field-user-role"
              label={t('roleplay.detail.fieldUserRole')}
              value={resolve(slot.userRole)}
            />
            <Field
              testID="roleplay-detail-field-ai-role"
              label={t('roleplay.detail.fieldAiRole')}
              value={resolve(slot.aiRole)}
            />
            <Field
              testID="roleplay-detail-field-first-speaker"
              label={t('roleplay.detail.fieldFirstSpeaker')}
              value={firstSpeakerLabel}
            />
          </SectionCard>

          {/* Error banner (for restart/remove failures) */}
          {errorInfo && (
            <View
              testID="roleplay-detail-error-banner"
              style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error, marginHorizontal: 16, marginBottom: 8 }]}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: 14 * fontScale, color: theme.text, ...ffs }}
              >
                {errorMessage(errorInfo)}
              </Text>
            </View>
          )}

          <View style={styles.buttonRow}>
            <ActionButton
              testID="roleplay-detail-button-open"
              label={t('roleplay.detail.openButton')}
              onPress={handleOpen}
              disabled={mutating}
              variant="primary"
            />
            <ActionButton
              testID="roleplay-detail-button-restart"
              label={t('roleplay.detail.restartButton')}
              onPress={() => setShowRestartDialog(true)}
              disabled={mutating}
              variant="secondary"
            />
            <ActionButton
              testID="roleplay-detail-button-remove"
              label={t('roleplay.detail.removeButton')}
              onPress={() => setShowRemoveDialog(true)}
              disabled={mutating}
              variant="destructive"
            />
          </View>
        </ScrollView>
      </WebContainer>

      <ConfirmDialog
        visible={showRestartDialog}
        title={t('roleplay.detail.restartConfirmTitle')}
        message={t('roleplay.detail.restartConfirmBody')}
        confirmLabel={t('roleplay.detail.restartConfirmButton')}
        cancelLabel={t('roleplay.detail.cancelButton')}
        onConfirm={handleRestartConfirm}
        onCancel={() => setShowRestartDialog(false)}
      />
      <ConfirmDialog
        visible={showRemoveDialog}
        title={t('roleplay.detail.removeConfirmTitle')}
        message={t('roleplay.detail.removeConfirmBody')}
        confirmLabel={t('roleplay.detail.removeConfirmButton')}
        cancelLabel={t('roleplay.detail.cancelButton')}
        destructive
        onConfirm={handleRemoveConfirm}
        onCancel={() => setShowRemoveDialog(false)}
      />
    </View>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function Field({ testID, label, value }: { testID: string; label: string; value: string }) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  return (
    <View testID={testID} style={{ marginBottom: 12 }}>
      <Text
        allowFontScaling={false}
        style={{ fontSize: 12 * fontScale, color: theme.textSecondary, marginBottom: 2, textTransform: 'uppercase', ...ffs }}
      >
        {label}
      </Text>
      <Text
        allowFontScaling={false}
        style={{ fontSize: 14 * fontScale, color: theme.text, ...ffs }}
      >
        {value}
      </Text>
    </View>
  );
}

interface ActionButtonProps {
  testID: string;
  label: string;
  onPress: () => void;
  disabled: boolean;
  variant: 'primary' | 'secondary' | 'destructive';
}

function ActionButton({ testID, label, onPress, disabled, variant }: ActionButtonProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();

  const backgroundColor =
    variant === 'primary'
      ? appColors.primary
      : variant === 'destructive'
        ? theme.error
        : theme.card;

  const textColor = variant === 'secondary' ? theme.text : '#FFFFFF';
  const borderColor = variant === 'secondary' ? theme.border : 'transparent';

  return (
    <TouchableOpacity
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.75}
      style={[
        styles.actionButton,
        { backgroundColor, borderColor, borderWidth: variant === 'secondary' ? 1 : 0 },
        disabled && { opacity: 0.6 },
      ]}
    >
      <Text
        allowFontScaling={false}
        numberOfLines={1}
        style={{ fontSize: 16 * fontScale, fontWeight: '600', color: textColor, ...ffs }}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  content: {
    paddingVertical: 16,
    gap: 4,
  },
  buttonRow: {
    gap: 10,
    paddingHorizontal: 16,
    marginTop: 8,
  },
  actionButton: {
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorBanner: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
});
